import Logo from '../../img/logo-wide.png';
import PropTypes from 'prop-types';
import React, { useState, useContext, Fragment } from 'react';
import { UserContext } from '../../lib/usercontext';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
    UserCircleIcon,
    Bars3Icon,
    //Cog6ToothIcon,
    XMarkIcon,
    ShoppingCartIcon,
    TruckIcon,
    UserGroupIcon,
    MapIcon,
    ListBulletIcon,
    BookOpenIcon,
    BuildingOffice2Icon,
    QuestionMarkCircleIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { signOut } from '../../lib/cognitoutils';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../routes';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const AdminLayout = ({ children, pageTitle }) => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { user } = useContext(UserContext);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchQueryParam = queryParams.get('search');
    const currentPath = location.pathname;

    const currentRoute = routes.find(route => route.path === location.pathname);
    const supportsSearch = currentRoute ? currentRoute.supportsSearch : false;

    const [searchQuery, setSearchQuery] = useState(searchQueryParam || '');

    const navigation = [
        { name: 'Deliveries', href: '/deliveries', icon: TruckIcon, current: currentPath === '/deliveries' },
        { name: 'Orders', href: '/orders', icon: ShoppingCartIcon, current: currentPath === '/orders' },
        { name: 'Routes', href: '/routes', icon: MapIcon, current: currentPath === '/routes' },
        { name: 'Menus', href: '/menus', icon: BookOpenIcon, current: currentPath === '/menus' },
        { name: 'Menu Items', href: '/items', icon: ListBulletIcon, current: currentPath === '/items' },
        { name: 'Clients', href: '/clients', icon: UserGroupIcon, current: currentPath === '/clients' },
        { name: 'Providers', href: '/providers', icon: BuildingOffice2Icon, current: currentPath === '/providers' },
        { name: 'Reports', href: '/reports', icon: ChartBarIcon, current: currentPath === '/reports' },
        { name: 'Users', href: '/users', icon: UserCircleIcon, current: currentPath === '/users' },
        { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon, current: currentPath === '/help' }
    ];

    const userNavigation = [
        { name: 'Account Settings', href: '/account' }
    ];

    const [, setIsLoggedIn] = useState(false);

    const handleSignout = async (event) => {
        event.preventDefault();
        try {
            await signOut();
            setIsLoggedIn(false);
            navigate('/login')
        } catch (error) {
            console.error('Login failed:', error);
        }
    };


    return (
        <div>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>
                                {/* Sidebar component, swap this element with another sidebar if you like */}
                                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                                    <div className="flex h-16 shrink-0 items-center">
                                        <a
                                            href="/dashboard"><img
                                                className="h-8 w-auto"
                                                src={Logo}
                                                alt="Five Star Logo"
                                            /></a>
                                    </div>
                                    <nav className="flex flex-1 flex-col">
                                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                            <li>
                                                <ul role="list" className="-mx-2 space-y-1">
                                                    {navigation.map((item) => (
                                                        <li key={item.name}>
                                                            <a
                                                                href={item.href}
                                                                className={classNames(
                                                                    item.current
                                                                        ? 'bg-gray-50 text-indigo-600'
                                                                        : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
                                                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={classNames(
                                                                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                                                        'h-6 w-6 shrink-0'
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                                {item.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                            {/*<li className="mt-auto">
                                                <a
                                                    href="#"
                                                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                                                >
                                                    <Cog6ToothIcon
                                                        className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-indigo-600"
                                                        aria-hidden="true"
                                                    />
                                                    Settings
                                                </a>
                                                </li>*/}
                                        </ul>
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center">
                    <a href="/dashboard"><img
                            className="h-8 w-auto"
                            src={Logo}
                            alt="Five Star"
                        /></a>
                    </div>
                    <nav className="flex flex-1 flex-col">
                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                            <li>
                                <ul role="list" className="-mx-2 space-y-1">
                                    {navigation.map((item) => (
                                        <li key={item.name}>
                                            <a
                                                href={item.href}
                                                className={classNames(
                                                    item.current
                                                        ? 'bg-gray-50 custom-blue-dark'
                                                        : 'text-gray-700 hover:text-custom-blue hover:bg-gray-50',
                                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                                )}
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        item.current ? 'text-custom-blue' : 'text-gray-400 group-hover:text-custom-blue',
                                                        'h-6 w-6 shrink-0'
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {item.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            {/*<li className="mt-auto">
                                <a
                                    href="#"
                                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-custom-blue"
                                >
                                    <Cog6ToothIcon
                                        className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-custom-blue"
                                        aria-hidden="true"
                                    />
                                    Settings
                                </a>
                                </li>*/}
                        </ul>
                    </nav>
                </div>
            </div>

            <div className="lg:pl-72">
                <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                    <button type="button" className="-m-2.5 p-2.5 text-gray-700 lg:hidden" onClick={() => setSidebarOpen(true)}>
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>

                    {/* Separator */}
                    <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

                    <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                        {supportsSearch && (
                            <form className="relative flex flex-1" action="#" method="GET">
                                <label htmlFor="search-field" className="sr-only">
                                    Search
                                </label>
                                <MagnifyingGlassIcon
                                    className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400"
                                    aria-hidden="true"
                                />
                                <input
                                    id="search-field"
                                    className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    type="search"
                                    name="search"
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </form>
                        )}
                        {!supportsSearch && (
                            <div className="flex flex-1 gap-x-4 self-stretch items-center lg:gap-x-6">
                                {pageTitle && (
                                    <h1 className="text-lg font-semibold text-gray-600">
                                        {pageTitle}
                                    </h1>
                                )}
                            </div>
                        )}
                        <div className="flex items-center gap-x-4 lg:gap-x-6">
                            {/* Separator */}
                            <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />

                            {/* Profile dropdown */}
                            <Menu as="div" className="relative">
                                <Menu.Button className="-m-1.5 flex items-center p-1.5">
                                    <span className="sr-only">Open user menu</span>
                                    <UserCircleIcon className="ml-1 h-8 w-8 rounded-full text-gray-400" aria-hidden="true" />
                                    <span className="hidden lg:flex lg:items-center">
                                        <span className="ml-4 text-sm font-semibold leading-6 text-gray-900" aria-hidden="true">
                                            {user.name}
                                        </span>
                                        <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </span>
                                </Menu.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                                        {userNavigation.map((item) => (
                                            <Menu.Item key={item.name}>
                                                {({ active }) => (
                                                    <a
                                                        href={item.href}
                                                        className={classNames(
                                                            active ? 'bg-gray-50' : '',
                                                            'block px-3 py-1 text-sm leading-6 text-gray-900'
                                                        )}
                                                    >
                                                        {item.name}
                                                    </a>
                                                )}
                                            </Menu.Item>
                                        ))}
                                        <Menu.Item>
                                            {({ active }) => (
                                                <a href="/"
                                                    onClick={handleSignout}
                                                    className={classNames(
                                                        active ? 'bg-gray-50' : '',
                                                        'block px-3 py-1 text-sm leading-6 text-gray-900'
                                                    )}
                                                >
                                                    Sign out
                                                </a>
                                            )}
                                        </Menu.Item>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    </div>
                </div>

                <main className="pb-8 pt-4">
                    <div className="px-4 sm:px-6 lg:px-2">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    )
}

AdminLayout.propTypes = {
    children: PropTypes.node.isRequired,
    pageTitle: PropTypes.string,
};


export default AdminLayout;
